import React, {PureComponent} from "react"
import debounce from "debounce-promise"

import UsersForBusiness, {userList, getNextPage, UsersForBusinessVariables} from "@irati/identity/apollo/query/UsersForBusiness"
import UsersForInternal, {userList as userListInternal, UsersForInternalVariables} from "@irati/identity/apollo/query/UsersForInternal"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"

/* eslint-disable no-restricted-imports */
/** @description: Tech debt, this should be moved outside the ui folder */
import authentication from "@irati/auth"
/* eslint-enable no-restricted-imports */

import {User} from "@irati/types"
import FormattedMessage from "@irati/utils/lang"

import {StringSchema} from "@irati/utils/validation"
import ProfilePic from "@irati/ui/src/ProfilePicture"
import Select, {SelectProps} from "@irati/ui/src/FormItem/Select"
import {Label} from "../Typography"
import Flex from "../Flex"
import getUserSearchText from "./UserSearchText"
import UserRowDeactivated from "./UserRow/Deactivated"

export type UserSelectOnChange = (value?: string | null, user?: User[] | null) => any

interface QueryForUsersProps extends UsersForInternalVariables, UsersForBusinessVariables {}
/* eslint-disable max-len */
interface Props
  extends Pick<
    SelectProps,
    | "id"
    | "name"
    | "label"
    | "hideLabel"
    | "placeholder"
    | "value"
    | "isDisabled"
    | "autoFocus"
    | "controlShouldRenderValue"
    | "hint"
    | "defaultValue"
  > {
  // data props
  businessId?: QueryForUsersProps["businessId"]
  removeSelf?: boolean
  adminsOnly: QueryForUsersProps["adminsOnly"]
  managersOnly?: QueryForUsersProps["managersOnly"]
  enabledOnly: QueryForUsersProps["enabled"]
  isMulti?: boolean
  teamIds: QueryForUsersProps["teamIds"]
  allUsers?: boolean
  isClearable?: boolean
  skipRecipients?: string[]
  includeTeams?: boolean

  // rest
  onChange: UserSelectOnChange
  recipients?: string[]
  validation?: StringSchema
}

interface State {
  variables: QueryForUsersProps
  searchString?: string
}

const MIN_SEARCH_LENGTH = 3

export default class UserSearch extends PureComponent<Props, State> {
  static getDerivedStateFromProps = (props: Props, state: State) => {
    const variables: QueryForUsersProps = {
      ...state.variables,
      managersOnly: props.managersOnly,
      adminsOnly: props.adminsOnly,
      teamIds: props.teamIds,
      friendsFamilyOnly: props.friendsFamilyOnly
    }

    if (!props.allUsers) {
      variables.includeTeams = props.includeTeams
      variables.businessId = props.businessId || authentication.business().uuid
    }

    if (props.enabledOnly !== false) {
      variables.enabled = true
    }

    return {variables}
  }

  constructor(props: Props) {
    super(props)
    let variables = {}
    if (!this.props.allUsers) {
      variables = {businessId: props.businessId || authentication.business().uuid}
    } else {
      variables = {includeBusiness: true}
    }
    this.state = {
      variables
    }

    this.updateSearch = debounce(this.updateSearch, 500)
  }

  private getPromptText = () => {
    const {searchString} = this.state
    if (searchString && searchString.length >= MIN_SEARCH_LENGTH) {
      return <FormattedMessage id="platform.global.noUsersFound" />
    }
    return <FormattedMessage id="platform.fields.selectRecipient.underCharLimit" />
  }

  private shouldIncludeUser = (option: {data: User}) => {
    if ((this.state.searchString?.length || MIN_SEARCH_LENGTH + 1) < MIN_SEARCH_LENGTH) {
      return false
    }
    if (this.props.removeSelf) {
      const currentUser = authentication.user()
      return currentUser.uuid !== option.data.uuid
    }

    return true
  }

  private handleChange: UserSelectOnChange = (key, object) => {
    return this.props.onChange(key, object)
  }

  private updateSearch = (searchString?: string | null) =>
    this.setState(({variables}) => {
      if ((searchString?.length || 0) >= MIN_SEARCH_LENGTH) {
        return {variables: {...variables, searchString}}
      } else {
        return {variables: {...variables, searchString: undefined}}
      }
    })

  private handleSearch: SelectProps["onInputChange"] = (searchString, {action}) => {
    if (action === "input-change") {
      this.setState({searchString}, () => {
        this.updateSearch(searchString)
      })
    } else if (action === "set-value" || action === "input-blur") {
      this.setState({searchString: undefined}, () => {
        this.updateSearch(null)
      })
    }
  }

  private filterOptions = (apollo: ApolloState) => {
    const {recipients, allUsers} = this.props
    const users = (allUsers ? userListInternal : userList)(apollo).results
    if (recipients) {
      return users.filter((user) => !recipients.find((uuid) => uuid === user.uuid))
    }
    if (this.props.skipRecipients) {
      return users.filter((user) => !this.props.skipRecipients?.includes(user.uuid))
    }
    return users
  }

  render() {
    const {variables, searchString} = this.state
    const selectProps = {
      id: this.props.id,
      name: this.props.name,
      isMulti: this.props.isMulti,
      label: this.props.label,
      hideLabel: this.props.hideLabel,
      placeholder: this.props.placeholder,
      value: this.props.value,
      isDisabled: this.props.isDisabled,
      autoFocus: this.props.autoFocus,
      controlShouldRenderValue: this.props.controlShouldRenderValue,
      hint: this.props.hint,
      isClearable: this.props.isClearable,
      defaultValue: this.props.defaultValue,
      flush: this.props.flush
    }
    const {validation, allUsers} = this.props
    const UsersQuery = allUsers ? UsersForInternal : UsersForBusiness
    return (
      <UsersQuery useLoader={false} useError={false} variables={variables}>
        {(apollo) => (
          <Select
            {...selectProps}
            options={this.filterOptions(apollo)}
            inputValue={searchString}
            onInputChange={this.handleSearch}
            filterOption={this.shouldIncludeUser}
            onMenuScrollToBottom={() => getNextPage(apollo, variables)}
            isLoading={apollo.loading}
            onChange={this.handleChange}
            getOptionLabel={(user: User) => firstnameLastname(user)}
            getOptionValue={(user: User) => user.uuid}
            autoComplete={false}
            aria-label={this.props["aria-label"]}
            validation={validation || null}
            components={{
              OptionIcon: (user: User) => {
                return <ProfilePic user={user} marginRight small deactivated={!user.isEnabled} />
              },
              OptionLabel: (user: User) => {
                return (
                  <Flex justifyContent="flex-start" direction="column" alignContent="center">
                    <div>
                      {firstnameLastname(user)} <UserRowDeactivated isEnabled={user.isEnabled!} />
                    </div>
                    {allUsers && <Label>({user.business?.businessName})</Label>}
                  </Flex>
                )
              },
              NoOptionsMessage: getUserSearchText(this.getPromptText())
            }}
          />
        )}
      </UsersQuery>
    )
  }
}
